<template>
  <div v-if="shouldDisplayErrors" class="cat-error-wrapper">
    <template v-if="hasErrors && !html">
      <template v-for="(error, idx) in collated">
        <div
          v-if="error.messages.length > 0"
          :id="id ? id + '-alert-' + idx : null"
          :key="idx"
          :class="cssClass(error)"
          :role="notifyAlert ? 'alert' : null"
          :aria-live="notifyAlert ? politeness(error.level) : null"
          :tabindex="focusable ? '-1' : null"
          :data-label="getHeading(error.level)"
        >
          <div
            v-if="container == true"
            aria-hidden="true"
            class="errors-container-icon"
          ></div>

          <span class="visually-hidden">{{ alertSrText(error.level) }}</span>
          <div
            v-if="getHeading(error.level) || isShowMoreEnable(error)"
            ref="containerHeading"
            tabindex="-1"
            :aria-level="hLevel"
            role="heading"
            class="h5"
          >
            <template v-if="isShowMoreEnable(error)">
              <span>{{ getMaxErrorheading(error.total) }}</span>
            </template>
            <template v-else>
              <span>{{ getHeading(error.level) }}</span>
            </template>
          </div>
          <div
            v-for="(message, idx2) in error.messages"
            :key="'p' + idx2"
            class="errors-body"
            :data-label-type="alertDataLabelType(error.level)"
          >
            <div class="errors-body-icon" aria-hidden="true"></div>
            <div class="error-text">
              <div v-if="message.title" class="error-text-title">
                <strong>{{ message.title }}</strong>
              </div>

              <p v-if="linkErrors">
                <button
                  type="button"
                  class="small simple link pn error-link"
                  :data-label="message.msg"
                  @click="errorLink(message.id)"
                  @keyup.enter="errorLink(message.id)"
                >
                  {{ message.msg }}
                </button>
              </p>
              <p v-else>{{ message.msg }}</p>
              <cat-btn
                v-if="
                  message.actionObject &&
                  message.actionObject.label &&
                  message.actionObject.link
                "
                link
                simple
                small
                :label="message.actionObject.label"
                @ui-click="errorAction(message.actionObject)"
              />
            </div>
            <div
              v-if="
                message.actionObject &&
                message.actionObject.label &&
                !message.actionObject.link
              "
              class="cat-alert-buttons"
            >
              <cat-btn
                small
                secondary
                :label="message.actionObject.label"
                @ui-click="errorAction(message.actionObject)"
              />
            </div>
          </div>
          <div
            v-if="isShowMoreEnable(error) && isShowMoreBtnVisible(error)"
            class="cat-alert-show-more-btn"
          >
            <cat-btn
              link
              :label="getShowMoreLabel"
              @ui-click="showMoreClick(error.level)"
            />
          </div>
        </div>
      </template>
    </template>

    <div
      v-if="html && parseInt(errorLevel) >= 0"
      :class="cssClass(parseInt(errorLevel))"
      role="alert"
      :aria-live="politeness(parseInt(errorLevel))"
      :data-label-type="alertDataLabelType(parseInt(errorLevel))"
    >
      <div
        v-if="container == true"
        aria-hidden="true"
        class="errors-container-icon"
      ></div>
      <slot></slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import services from "@/components/services.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", services.resolveTxt);
    const getService = inject("getService", services.getService);
    const suppressErrors = inject("suppressErrors", false);
    const headingLevel = inject("headingLevel", null);

    return {
      resolveTxt,
      suppressErrors,
      headingLevel,
      getService
    };
  },
  name: "CatErrors",
  props: {
    id: {
      type: [String, Number],
      description:
        "The node the error container is associated to. This will be assigned to 'id-errors' if this is a container."
    },
    errors: {
      type: Array,
      description:
        "Array of object with properties 'id', 'level', 'msg', 'title', or 'actionObject' containing action details. The id property is used when 'linkErrors' is set to true to jump to the element related to this. Example: [{ id: 'id-of-the-node', level: 1, msg: 'Name is required.' }]"
    },
    container: {
      type: Boolean,
      default: false,
      description: "Renders the alert box container for all the error messages."
    },
    html: {
      type: Boolean,
      default: false,
      description:
        "Allows to render a custom error content using the default slot."
    },
    errorLevel: {
      type: [Number, String],
      default: -1,
      description:
        "Applicable if 'html' is set to true. The alert styling applied to the alert message wrapper. If a container, the alert styling is applied to the container box."
    },
    linkErrors: {
      type: Boolean,
      default: false,
      description: "Renders each error message as a link."
    },
    levelHeadings: {
      type: Object,
      description:
        "Object containing the text for the alert headings. The object properties can be any of the following: error, errorSingle, warn, warnSingle, info, infoSingle"
    },
    showMore: {
      type: Boolean,
      default: false,
      description:
        "Renders a limited number of alert messages per alert level container and displays a show more button to show the remaining alert messages. See the errorLimit parameter for the limit."
    },
    errorLimit: {
      type: Number,
      default: 5,
      description: "Limits the maximum default error message on a alert box."
    },
    maxErrorHeading: {
      type: String,
      description: "Renders custom heading when show more is enabled."
    },
    showMoreLabel: {
      type: String,
      description: "Allows to render a custom show more button text."
    },
    disableDispatch: {
      type: Boolean,
      default: false,
      description: "Display error by default if error is coming from repeater."
    },
    forceNotifyAlert: {
      type: Boolean,
      default: false,
      description:
        "Forces notification alert behavior regardless of container and linkErrors settings."
    }
  },
  data: function () {
    return {
      hLevel: this.headingLevel ? this.headingLevel + 1 : 3,
      errorCount: [],
      refreshMsg: false,
      displayErrorBlock: true,
      deferredErrors: false,
      isInsideModal: false,
      modalObserver: null
    };
  },
  watch: {
    errors: {
      handler(newErrors, oldErrors) {
        if (this.displayErrorBlock && !newErrors && this.isShowOnSubmit()) {
          // Reset displayErrorBlock when there is no error.
          this.displayErrorBlock = false;
        }

        // Handle modal deferral for new errors
        if (newErrors && this.isModalCurrentlyOpen() && !this.isInsideModal) {
          this.deferredErrors = true;
        } else if (
          newErrors &&
          (!this.isModalCurrentlyOpen() || this.isInsideModal)
        ) {
          this.deferredErrors = false;
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted: function () {
    this.showOnSubmit();
    this.updateModalContainment();

    if (this.hasErrors) {
      this.checkModalDeferral();
    }

    // Observe modal-open class changes to handle rapid modal open/close cycles
    this.setupModalObserver();
  },
  beforeUnmount: function () {
    this.removeModalObserver();
  },
  computed: {
    hasErrors: function () {
      return this.errors != undefined && this.errors.length != 0;
    },
    shouldDisplayErrors: function () {
      const baseConditions = !this.suppressErrors && this.displayErrorBlock;

      if (this.deferredErrors) {
        return false;
      }

      if (
        this.hasErrors &&
        this.isModalCurrentlyOpen() &&
        !this.isInsideModal
      ) {
        return false;
      }

      return baseConditions;
    },
    containerCssClass: function () {
      return {
        // fieldset: this.container,
        catErrors: !this.container
      };
    },
    focusable: function () {
      // If the error is a block error message (not the collated), it is necessary to make this focusable
      return this.id && this.container && !this.linkErrors;
    },
    notifyAlert: function () {
      if (this.forceNotifyAlert) {
        return true;
      }
      return this.container && this.linkErrors ? false : true;
    },
    collated: function () {
      let msgList = [
        {
          level: 1,
          messages: this.errors.filter(function (x) {
            return x.level === 1 || x.level === 0;
          })
        },
        {
          level: 2,
          messages: this.errors.filter(function (x) {
            return x.level === 2;
          })
        },
        {
          level: 3,
          messages: this.errors.filter(function (x) {
            return x.level === 3;
          })
        },
        {
          level: 4,
          messages: this.errors.filter(function (x) {
            return x.level === 4;
          })
        }
      ];

      if (this.showMore) {
        let self = this;
        let items = [];
        this.refreshMsg;

        msgList.forEach((msg) => {
          msg.total = msg.messages.length;

          if (!self.errorCount[msg.level]) {
            self.errorCount[msg.level] = self.errorLimit;
          }

          if (msg.messages.length < self.errorCount[msg.level]) {
            self.errorCount[msg.level] = msg.messages.length;
          }

          msg.messages = msg.messages.filter(
            (newMessage, idx) => idx < self.errorCount[msg.level]
          );

          items.push(msg);
        });
      }

      return msgList;
    },
    getShowMoreLabel: function () {
      return this.showMoreLabel
        ? this.showMoreLabel
        : this.resolveTxt("skin.alert.show.more.button");
    }
  },
  methods: {
    cssClass: function (error) {
      let level = error.level;
      return {
        "cat-alert": this.container,
        catError: !this.container,
        error: level === 1 || level === 0,
        warn: level === 2,
        info: level === 3,
        debug: level === 4,
        default: level === 5,
        "cat-alert-show-more": this.isShowMoreEnable(error)
      };
    },
    getHeading: function (level) {
      if (!this.levelHeadings) {
        return null;
      }
      var _t = this;
      var getLevelMessages = function (level) {
        var messages = [];
        _t.collated.some(function (error) {
          if (error.level === level) {
            messages = error.messages;
            return true;
          } else {
            return false;
          }
        });
        return messages;
      };
      var messages = getLevelMessages(level);
      var heading = null;
      if (level === 0 || level === 1) {
        heading =
          messages.length > 1
            ? this.levelHeadings.error
            : this.levelHeadings.errorSingle;
      } else if (level === 2) {
        heading =
          messages.length > 1
            ? this.levelHeadings.warn
            : this.levelHeadings.warnSingle;
      } else if (level === 3) {
        heading =
          messages.length > 1
            ? this.levelHeadings.info
            : this.levelHeadings.infoSingle;
      }

      if (heading) {
        heading = heading.replace(/\{0}/g, messages.length);
      }
      return heading;
    },
    isShowMoreEnable: function (error) {
      return error.total > this.errorLimit && this.showMore;
    },
    isShowMoreBtnVisible: function (error) {
      return this.errorCount[error.level] < error.total;
    },
    showMoreClick: function (level) {
      this.errorCount[level] += this.errorLimit;
      this.refreshMsg = !this.refreshMsg;
    },
    getMaxErrorheading: function (total) {
      let heading = this.maxErrorHeading
        ? this.maxErrorHeading
        : this.resolveTxt("skin.alert.show.more.heading");
      return heading.replace("{0}", total);
    },
    politeness: function (level) {
      // We are not returning assertive to prevent redundant setting for role alerts
      return level < 0 || level >= 3 ? "polite" : null;
    },
    hasLevel: function (level, errors) {
      for (var e in errors) {
        if (errors[e].level === level) {
          return true;
        }
      }
      return false;
    },
    errorAction: function (actionObject) {
      this.$emit("ui-click", actionObject);
    },
    errorLink: function (id) {
      this.$emit("ui-click", { id: id });
    },
    alertDataLabelType: function (level) {
      switch (level) {
        case 0:
        case 1:
          return "alert-error";
        case 2:
          return "alert-warning";
        case 3:
          return "alert-info";
        case 4:
          return "alert-success";
        default:
          return "alert-default";
      }
    },
    alertSrText: function (level) {
      switch (level) {
        case 0:
        case 1:
          return this.resolveTxt(
            "skin.alert.icon.offscreenLabel.error",
            "Error"
          );
        case 2:
          return this.resolveTxt(
            "skin.alert.icon.offscreenLabel.warning",
            "Warning"
          );
        case 3:
          return this.resolveTxt(
            "skin.alert.icon.offscreenLabel.info",
            "Information"
          );
        case 4:
          return this.resolveTxt(
            "skin.alert.icon.offscreenLabel.success",
            "Success"
          );
        default:
          return this.resolveTxt(
            "skin.alert.icon.offscreenLabel.default",
            "Note"
          );
      }
    },
    showOnSubmit: function () {
      if (this.isShowOnSubmit()) {
        const _this = this;

        // Toggle display onload based from errors
        this.displayErrorBlock = this.hasErrors;

        document.addEventListener("ui-show-error-block", function (env) {
          // Show error on submit
          _this.displayErrorBlock = env.detail.hasErrors;
        });
      }
    },
    /**
     * isShowOnSubmit
     * Returns true if all condition below are met
     * service.displayErrorOnSubmit is true
     * Props this.container is true
     * Props this.disableDispatch is false
     * Props this.levelHeadings has value
     * Method isDisabledInRecord() is false
     * @returns {Boolean}
     */
    isShowOnSubmit: function () {
      // Always visible in record and all child
      if (this.isDisabledInRecord()) {
        return false;
      } else {
        const service = this.getService();
        const displayErrorOnSubmit = !!(service
          ? service.displayErrorOnSubmit
          : true);

        return (
          displayErrorOnSubmit &&
          this.container &&
          !this.disableDispatch &&
          typeof this.levelHeadings !== "undefined"
        );
      }
    },
    /**
     * isDisabledInRecord
     * Ability to disable from record and any child
     * Useful for case like CTR. Search for keyValue 'ui-errors-summary-block-only-on-submit-disabled' to see sample implementation
     * @returns {Boolean}
     */
    isDisabledInRecord: function () {
      // Check if any record/box has the data attribute
      // data-errors-summary-block-only-on-submit-disable
      const getDisableAttribute = (selector) =>
        document
          .querySelector(selector)
          ?.closest("[data-errors-summary-block-only-on-submit-disable]")
          ?.getAttribute("data-errors-summary-block-only-on-submit-disable");

      return (
        getDisableAttribute(".cat-box") ??
        getDisableAttribute(".glue-record") ??
        false
      );
    },
    /**
     * isModalCurrentlyOpen
     * Checks if any modal is currently open by looking for the modal-open class on body
     * This leverages the existing modalMixin infrastructure
     * @returns {Boolean}
     */
    isModalCurrentlyOpen: function () {
      return document.body.classList.contains("modal-open");
    },
    /**
     * checkModalDeferral
     * Checks if errors should be deferred based on modal state
     */
    checkModalDeferral: function () {
      this.deferredErrors =
        this.hasErrors && this.isModalCurrentlyOpen() && !this.isInsideModal;
    },
    /**
     * updateModalContainment
     * Updates the isInsideModal flag based on current DOM position
     */
    updateModalContainment: function () {
      this.isInsideModal = this.isErrorsContainedInModal();
    },
    /**
     * isErrorsContainedInModal
     * Checks if this error component is contained within a modal by traversing up the DOM
     * Returns true if the component is inside a .modal element
     * @returns {Boolean}
     */
    isErrorsContainedInModal: function () {
      // Get the component's root element
      const errorElement = this.$el;
      if (!errorElement) {
        return false;
      }

      // Traverse up the DOM to look for a modal container
      let currentElement = errorElement.parentElement;
      while (currentElement && currentElement !== document.body) {
        // Check for common modal class names
        if (
          currentElement.classList.contains("modal") ||
          currentElement.classList.contains("modal-dialog") ||
          currentElement.classList.contains("modal-content")
        ) {
          return true;
        }
        currentElement = currentElement.parentElement;
      }

      return false;
    },
    /**
     * setupModalObserver
     * Sets up MutationObserver to watch for modal-open class changes on body
     */
    setupModalObserver: function () {
      const self = this;
      this.modalObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class"
          ) {
            self.$nextTick(() => {
              self.handleModalStateChange();
            });
          }
        });
      });

      // Start observing body element for class attribute changes
      this.modalObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"]
      });
    },
    /**
     * removeModalObserver
     * Removes MutationObserver for modal state changes
     */
    removeModalObserver: function () {
      if (this.modalObserver) {
        this.modalObserver.disconnect();
        this.modalObserver = null;
      }
    },
    /**
     * handleModalStateChange
     * Handles modal state changes to ensure deferred errors are shown when modal closes
     */
    handleModalStateChange: function () {
      // Only process modal state changes if this component has errors or deferred errors
      // This prevents multiple CatErrors components from interfering with each other
      if (!this.hasErrors && !this.deferredErrors) {
        return;
      }

      // If modal just opened and we have errors (and not inside modal), defer them
      if (
        this.isModalCurrentlyOpen() &&
        this.hasErrors &&
        !this.isInsideModal &&
        !this.deferredErrors
      ) {
        this.deferredErrors = true;
      }

      // If we have deferred errors and modal is now closed, show them
      if (this.deferredErrors && !this.isModalCurrentlyOpen()) {
        this.deferredErrors = false;
        // In showOnSubmit mode, we need to also enable displayErrorBlock
        // when showing deferred errors after modal close
        if (this.isShowOnSubmit() && this.hasErrors) {
          this.displayErrorBlock = true;
        }
      }
    }
  }
});
</script>
